import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import {
  Newspaper, GraduationCap, HeartPulse, Apple, Scissors, Brain,
  Film, ExternalLink, Globe, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  news: { icon: Newspaper, color: 'text-blue-600 bg-blue-50', label: 'News' },
  training: { icon: GraduationCap, color: 'text-amber-600 bg-amber-50', label: 'Training' },
  health: { icon: HeartPulse, color: 'text-red-500 bg-red-50', label: 'Health' },
  nutrition: { icon: Apple, color: 'text-green-600 bg-green-50', label: 'Nutrition' },
  grooming: { icon: Scissors, color: 'text-purple-600 bg-purple-50', label: 'Grooming' },
  behavior: { icon: Brain, color: 'text-indigo-600 bg-indigo-50', label: 'Behavior' },
  entertainment: { icon: Film, color: 'text-pink-600 bg-pink-50', label: 'Fun' },
  general: { icon: Globe, color: 'text-gray-600 bg-gray-50', label: 'General' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function CommunityFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchFeed();
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [filter]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/api/feed/categories`);
      if (res.ok) setCategories(await res.json());
    } catch (err) { /* silent */ }
  };

  const fetchFeed = async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? `${API}/api/feed/public?limit=12`
        : `${API}/api/feed/public?limit=12&category=${filter}`;
      const res = await fetch(url);
      if (res.ok) setItems(await res.json());
    } catch (err) { /* silent */ }
    setLoading(false);
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div data-testid="community-feed">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">What's Happening</h2>
      </div>

      {/* Category filter pills */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide" data-testid="feed-filters">
          {categories.slice(0, 8).map(cat => (
            <button
              key={cat.id}
              onClick={() => setFilter(cat.id)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === cat.id
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`feed-filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Feed items */}
      <div className="space-y-3" data-testid="feed-items">
        {items.map(item => {
          const catConfig = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.general;
          const CatIcon = catConfig.icon;

          return (
            <a
              key={item.id}
              href={item.is_external ? item.url : undefined}
              onClick={e => {
                if (!item.is_external) {
                  e.preventDefault();
                  window.location.href = item.url;
                }
              }}
              target={item.is_external ? '_blank' : undefined}
              rel={item.is_external ? 'noopener noreferrer' : undefined}
              className="block group"
              data-testid={`feed-item-${item.id}`}
            >
              <Card className="hover:shadow-md transition-all hover:border-gray-300 group-hover:bg-gray-50/50">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 mt-0.5 p-2 rounded-lg ${catConfig.color}`}>
                      <CatIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs text-muted-foreground">{item.source_name}</span>
                        {item.is_external && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
                        <span className="text-xs text-muted-foreground">{timeAgo(item.created_at)}</span>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-1">
                        {item.title}
                      </h3>
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>
                      )}
                    </div>
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt=""
                        className="shrink-0 w-16 h-16 rounded-lg object-cover"
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            </a>
          );
        })}
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
